import { t } from '@lingui/core/macro'
import type { ChartDataPoint } from 'features/systemLens'
import { scoreContiguityLimitMs } from 'features/timeSeries/hooks/useSentinelScores'
import type { TimeSeriesDerivedValueEntryJSON } from 'types'
import { RolodexEntryType } from 'types'

import { agentKpiPenId } from './agentKpiPen'

/**
 * The saved model's own output — expected-at-conditions and the residual
 * (actual − expected) — merged into the chart's data as ordinary columns.
 *
 * These cannot be derived values in the normal sense: a derived value is a
 * formula over pens, and an expectation learned from a baseline period is data,
 * not a formula. But they are also NOT worth an agent round-trip: they were
 * computed when the model was fitted and saved with it, so showing them is
 * arithmetic on numbers we already have. This store is how the deterministic
 * path gets them onto the chart.
 */

export interface ModelSeriesInput {
  /** ISO stamps, as saved with the model: dates when graded daily, else times. */
  dates: string[]
  actual: (number | null)[]
  expected: (number | null)[]
  /** [start, end) spans of rows the fit did not use, from the server. */
  dropped?: string[][]
  /** The rolling window the series was smoothed with, in days. */
  smoothing_days?: number
}

/**
 * Whether a plotted timestamp is one the fit ignored.
 *
 * Blanking the value is not enough to make it disappear: the uPlot builder skips
 * a point with no numeric value instead of emitting a null, so the line is drawn
 * straight across the removed rows and nothing looks hidden. The chart breaks a
 * line on an explicit gap list, so the dropped stamps have to be handed over as
 * well — that is what actually makes a gated-out span read as absent.
 */
export const isRowDropped = (stamp: number): boolean =>
  !!loaded && isDropped(loaded.dropped, stamp)

/** The published model's dropped periods, for the chart to cut out of its lines. */
export const droppedRanges = (): Array<[number, number]> =>
  loaded ? loaded.dropped : []

/**
 * How wide a hole between graded buckets can be before it is an outage rather
 * than a skipped day. The wider of the series' own spacing limit (so a
 * sparsely graded model is not read as all holes) and its smoothing window
 * (a hole inside the window is already inside the smoothed value).
 */
export const gapToleranceMs = (
  stamps: number[],
  smoothingDays: number
): number =>
  Math.max(
    stamps.length > 1 ? scoreContiguityLimitMs(stamps) : DAY_MS,
    smoothingDays * DAY_MS
  )

/** [start, end) spans between graded buckets wider than the tolerance. */
export const ungradedHoles = (): Array<[number, number]> => {
  if (!loaded) return []
  const holes: Array<[number, number]> = []
  for (let i = 1; i < loaded.stamps.length; i++) {
    const from = loaded.stamps[i - 1] + loaded.bucketMs
    const to = loaded.stamps[i]
    if (to - loaded.stamps[i - 1] > loaded.gapToleranceMs)
      holes.push([from, to])
  }
  return holes
}

/** The three pens the loaded model draws, or none when nothing is loaded. */
export const modelPenIds = (): string[] =>
  loaded ? [loaded.actualId, loaded.expectedId, loaded.residualId] : []

/**
 * Whether a timestamp falls in a span the fit ignored. Binary search: the spans
 * are sorted and this runs once per plotted point, per render.
 */
const isDropped = (spans: Array<[number, number]>, stamp: number): boolean => {
  let lo = 0
  let hi = spans.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const [from, to] = spans[mid]
    if (stamp < from) hi = mid - 1
    else if (stamp >= to) lo = mid + 1
    else return true
  }
  return false
}

/**
 * A point with no series on it. The timestamp is kept so the axis still spans
 * the real window and the gap reads as a gap; `time` comes along because the
 * chart's own row shape requires it.
 */
const blank = (point: ChartDataPoint): ChartDataPoint =>
  ({ timestamp: point.timestamp, time: point.time }) as ChartDataPoint

interface Loaded {
  /**
   * The KPI pen. Its values come from the model's OWN series rather than from
   * evaluating the expression over raw tags, so the actual and the expectation
   * are the same measurement at the same resolution.
   *
   * Evaluating client-side put the KPI on screen at the historian's raw cadence
   * while the expectation sat at the model's bucket, which made the two
   * incomparable — and where the KPI is just a tag (no formula), it drew a
   * second copy of a pen already in the list. What the fit actually used is the
   * bucket median, and that is what belongs on the chart.
   */
  actualId: string
  expectedId: string
  residualId: string
  /** bucket start (epoch ms) → the values for that bucket. */
  byBucket: Map<
    number,
    { actual: number | null; expected: number | null; residual: number | null }
  >
  /** Width of one bucket in ms, so a point can be floored into one. */
  bucketMs: number
  /**
   * Half-open [start, end) spans of rows the fit did NOT use, from the server.
   *
   * Derived there and not here on purpose. The gate is evaluated against the
   * pulled frame's own columns — role names, the KPI, anything the server's
   * evaluator can see — and the browser has only the plotted tags. Re-deriving
   * it client-side meant a gate naming a column the chart could not resolve
   * evaluated to "unknown" for every row and hid nothing, which looks exactly
   * like a gate that dropped nothing. The server already knows the answer.
   */
  dropped: Array<[number, number]>
  /** Graded stamps, ascending. */
  stamps: number[]
  gapToleranceMs: number
}

let loaded: Loaded | null = null

const DAY_MS = 86_400_000

/**
 * Hide the rows the model threw away.
 *
 * A model grades only the rows that survive its gate and its masked ranges, so
 * the chart otherwise shows two populations at once: what the model judged, and
 * what it never looked at. Comparing a KPI line against pens that still include
 * gated-out hours is how a "step" turns out to be a shutdown. With this on,
 * every pen is blanked wherever the fit ignored the data — what you see is what
 * the fit saw.
 */
let hideExcluded = false
const hideListeners = new Set<() => void>()

export const setHideExcludedDays = (on: boolean): void => {
  if (hideExcluded === on) return
  hideExcluded = on
  hideListeners.forEach((listener) => listener())
}

export const getHideExcludedDays = (): boolean => hideExcluded

export const subscribeHideExcluded = (listener: () => void): (() => void) => {
  hideListeners.add(listener)
  return () => hideListeners.delete(listener)
}

export const expectedPenId = (entryId: string) => `derived_expected:${entryId}`
export const residualPenId = (entryId: string) => `derived_residual:${entryId}`

/** Publish (or clear) a model's saved series for the chart to merge. */
export const setModelSeries = (
  entryId: string | null,
  series: ModelSeriesInput | null | undefined
): void => {
  if (!entryId || !series?.dates?.length) {
    loaded = null
    return
  }
  const stamps = series.dates
    .map((date) => Date.parse(date))
    .filter((stamp) => Number.isFinite(stamp))
    .sort((a, b) => a - b)
  const bucketMs = inferBucketMs(stamps)
  const byBucket = new Map<
    number,
    { actual: number | null; expected: number | null; residual: number | null }
  >()
  series.dates.forEach((date, i) => {
    const stamp = Date.parse(date)
    if (!Number.isFinite(stamp)) return
    const expected = series.expected[i] ?? null
    const actual = series.actual[i] ?? null
    byBucket.set(Math.floor(stamp / bucketMs) * bucketMs, {
      actual,
      expected,
      residual:
        typeof expected === 'number' && typeof actual === 'number'
          ? actual - expected
          : null,
    })
  })
  loaded = {
    actualId: agentKpiPenId({ entry_id: entryId }),
    expectedId: expectedPenId(entryId),
    residualId: residualPenId(entryId),
    byBucket,
    bucketMs,
    dropped: (series.dropped ?? [])
      .map(([from, to]): [number, number] => [Date.parse(from), Date.parse(to)])
      .filter(([from, to]) => Number.isFinite(from) && Number.isFinite(to))
      .sort((a, b) => a[0] - b[0]),
    stamps,
    gapToleranceMs: gapToleranceMs(stamps, series.smoothing_days ?? 0),
  }
}

/**
 * The width of one point in the model's series, read off the series itself.
 *
 * The grading bucket is a model parameter now, so this cannot assume a day. It
 * is taken from the data rather than passed in because the series is the thing
 * being mapped: if the server graded hourly, the stamps are an hour apart, and
 * inferring it keeps the merge correct for a model saved before the parameter
 * existed. The smallest gap wins — a single outage must not be read as the
 * bucket width — and a one-point series falls back to a day.
 */
const inferBucketMs = (stamps: number[]): number => {
  let smallest = Number.POSITIVE_INFINITY
  for (let i = 1; i < stamps.length; i++) {
    const gap = stamps[i] - stamps[i - 1]
    if (Number.isFinite(gap) && gap > 0 && gap < smallest) smallest = gap
  }
  return Number.isFinite(smallest) ? smallest : DAY_MS
}

/**
 * Merge the published series into chart points. Runs AFTER derived values so
 * these columns win, and stamps a bucket's value onto every point inside that
 * bucket — the fit is a median over the bucket, so a step across it is what it
 * actually is. Grade the model hourly and you get an hourly expectation; the
 * chart is showing the resolution that was asked for, not one point per day.
 */
export const applyModelSeries = (
  data: ChartDataPoint[],
  /** Blank pens on days the model never graded. Passed in rather than read from
   *  the store so this stays pure and the caller's memo can depend on it. */
  hideExcludedDays = false
): ChartDataPoint[] => {
  if (!loaded || !data.length) return data
  const { actualId, expectedId, residualId, byBucket, bucketMs, dropped } =
    loaded
  return data.map((point) => {
    const stamp =
      typeof point.timestamp === 'number'
        ? point.timestamp
        : new Date(point.timestamp).getTime()
    const values = byBucket.get(Math.floor(stamp / bucketMs) * bucketMs)
    if (!values) {
      // A bucket the model never graded reads as a gap for the same reason.
      if (hideExcludedDays) return blank(point)
      // The merge owns these columns even where it has nothing to merge: the
      // derived-value pass has already evaluated their placeholder expression
      // to a literal 0, and left standing it draws a flat zero line outside
      // the graded span and drags the shared axis.
      return {
        ...point,
        [actualId]: null,
        [expectedId]: null,
        [residualId]: null,
      }
    }
    // A graded bucket can still contain rows the gate threw out.
    if (hideExcludedDays && isDropped(dropped, stamp)) {
      return blank(point)
    }
    return {
      ...point,
      [actualId]: values.actual,
      [expectedId]: values.expected,
      [residualId]: values.residual,
    }
  })
}

/**
 * Entries that make the two columns render as named pens. The expression is a
 * placeholder — `applyModelSeries` overwrites the column right after — but the
 * dependencies are real: the chart only renders a derived item when they are
 * all plotted, and these belong on the chart exactly when the KPI does.
 */
export const modelSeriesDerivedValues = (
  entryId: string,
  kpiName: string,
  dependencyIds: string[],
  siteId: string,
  include: { expected: boolean; residual: boolean }
): TimeSeriesDerivedValueEntryJSON[] => {
  const make = (id: string, name: string): TimeSeriesDerivedValueEntryJSON => ({
    id,
    type: RolodexEntryType.TIME_SERIES_DERIVED_VALUE,
    data: {
      name,
      expression: '0',
      unit: null,
      description: t`Saved output of agent model ${entryId}`,
      dependencyIds,
    },
    tags: { site: siteId },
  })
  const out: TimeSeriesDerivedValueEntryJSON[] = []
  if (include.expected) {
    out.push(make(expectedPenId(entryId), `${kpiName} expected`))
  }
  if (include.residual) {
    out.push(make(residualPenId(entryId), `${kpiName} residual`))
  }
  return out
}
